import Util from './util';

/**
 * Validate if the input is probably tabular data. This is the case if
 * the following rules are true:
 *
 * 1. Presence of tabs (\t) and new lines (\n) — required for Excel/Sheets copy.
 * 2. At least 2 columns.
 * 3. Consistent column counts — no wild variation.
 *
 * @param {string} input - The cleaned and normalized text
 * @returns {boolean} - whether the input is probably tabular data
 */
function isProbablyTabularData(input) {

    // 1. If there are no newlines and no tabs, we can stop processing
    if (!input.includes('\n') && !input.includes('\t')) {
        return false;
    }

    // 2. At least 2 columns
    const rows = input.split('\n').map(line => line.split('\t'));
    const numberOfColumns = rows[0].length;

    if (numberOfColumns < 2) {
        return false;
    }

    // 3. Has consistent columns, each row should have the same amount of colums
    const hasConsistentColumns = rows.filter(columns => columns.length === numberOfColumns).length === rows.length;

    if (!hasConsistentColumns) {
        return false;
    }

    return true;
}

function getClipboardAsString(event) {
    return (event.originalEvent || event).clipboardData.getData('text/plain');
}

export function parseClipboardAsTable(event, prevent = true, requireRectangular = true) {
    // TODO should have HTML cleaner processor
    const tableInput = Util.normalizeLineEndings(getClipboardAsString(event));

    if (requireRectangular && !isProbablyTabularData(tableInput)) {
        return false;
    }

    if (prevent) {
        event.preventDefault();
    }

    const parsedTable = []
    for (const row of tableInput.split('\n')) {
        const cells = row.split('\t')
        parsedTable.push(cells)
    }

    return parsedTable;
}

export function pastePostProcessInlineElements(pastedText = '') {
    const pasted = document.createElement('div')
    pasted.innerHTML = pastedText

    // Create a NodeIterator object to safely iterate through the DOM tree in serial instead of recursivly,
    // which could cause stack overflow problems with big DOM trees. Add a node filter that only returns the
    // element nodes and comment nodes, since we do not need to process the textual part of the pasted content,
    // only HTML markup and HTML comments.
    // eslint-disable-next-line no-bitwise
    const nodeIterator = document.createNodeIterator(pasted, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_COMMENT)
    // Process one element at the time.
    let currentNode
    // eslint-disable-next-line no-cond-assign
    while (currentNode = nodeIterator.nextNode()) {
        if (currentNode !== nodeIterator.root) {
            // Remove <meta> and <style> elements and comment nodes, common in text editors such as Word.
            if (['META', 'STYLE', '#comment'].includes(currentNode.nodeName)) {
                currentNode.remove()
                continue
            }
            const isExpression = currentNode.nodeName === 'IMG' && currentNode.classList.contains('js-expression')
            // Flatten elements that are not included in the expression below.
            if (!['STRONG', 'EM', 'B', 'I', 'U', 'SUB', 'SUP', 'BR', 'IMG'].includes(currentNode.nodeName)) {
                currentNode.outerHTML = currentNode.innerHTML
            }
            // Loop through attributes object and remove all items in array.
            for (let i = currentNode.attributes.length - 1; i >= 0; i--) {
                const attributeName = currentNode.attributes[i].name
                if (
                    isExpression &&
                    ['src', 'data-base64formula', 'class'].includes(attributeName)
                ) {
                    continue
                }
                currentNode.removeAttribute(attributeName)
            }
        }
    }
    return pasted.innerHTML
}
